import clsx from 'clsx';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import CustomIcon from 'assets/Icon';

import { EOwnerStatus, IOwner } from 'types/owner.types';

import './style.scss';

interface OwnerCardProps {
    ownerDetails: IOwner;
    index: number;
    onClickReview: (id: number, status: string, index: number) => void;
}

export default function OwnerCard({
  ownerDetails: { owner_name, fsm_name, performance, strategy },
  index,
  onClickReview }: OwnerCardProps,
) {
  function renderDueText(text: string) {
    const match = text.match(/^(Was due|Due|Sent|Skipped)/i);
    if (!match) {
      return text;
    }

    const prefix = match[0];
    const rest = text.slice(prefix.length).trimStart();

    return (
      <span className={clsx('owner-card-date-time', prefix === 'Was due' && 'was-due')}>
        <span className="owner-card-date-time-prefix">{prefix}</span>{' '}
        {rest}
      </span>
    );
  }
    
  const getPerformanceStackContent = () => {
    if (!performance) {
      return null;
    }

    if (performance.status === EOwnerStatus.SKIPPED) {
      return (
        <Stack direction="row" alignItems="center" gap={1}>
          {renderDueText(performance.label)}
          <CustomIcon name="archive" sx={{ fontSize: 18, color: '#636363' }} />
        </Stack>
      );
    }

    if (performance.status === EOwnerStatus.SENT) {
      return (
        <Stack direction="row" alignItems="center" gap={1}>
          {renderDueText(performance.label)}
          <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
        </Stack>
      );
    }

    return (
      <Stack direction="row" alignItems="center" gap={1}>
        {renderDueText(performance.label)}
        {(performance.status !== EOwnerStatus.REGENERATING && performance.status !== EOwnerStatus.SENDING) && (
          <Button
            variant="outlined"
            endIcon={<CustomIcon name="openIn" sx={{ fontSize: 16, color: '#0F244D' }} />}
            onClick={() => onClickReview(performance.id, performance.status, index)}
            sx={{
              fontSize: 12,
              color: '#0F314D',
              border: '1px solid #0F314D',
              padding: '6px 8px',
              borderRadius: 2,
              lineHeight: 'normal',
              gap: 1,
              textTransform: 'initial',
              '& .MuiButton-endIcon': { margin: 0 },
              '& .MuiButton-endIcon>*:nth-of-type(1)': { fontSize: 16  },
            }}
          >
            Review
          </Button>
        )}
      </Stack>
    );
  };

  const getStrategyStackContent = () => {
    if (!strategy) {
      return null;
    }

    if (strategy.status === EOwnerStatus.SENT) {
      return (
        <Stack direction="row" alignItems="center" gap={1}>
          {renderDueText(strategy.label)}
          <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
        </Stack>
      );
    }

    return (
      <Stack direction="row" alignItems="center" gap={1}>
        {renderDueText(strategy.label)}
      </Stack>
    );
  };

  const getTooltipTitle = (status: EOwnerStatus) => {
    if (status === EOwnerStatus.REGENERATING) {
      return 'Regenerating...';
    }
    if (status === EOwnerStatus.SENDING) {
      return 'Sending...';
    }
    if (status === EOwnerStatus.REGENERATED) {
      return 'Regenerated.';
    }
    if (status === EOwnerStatus.FAILED_TO_REGENERATE) {
      return 'Failed to regenerate. Please try again.';
    }
    if (status === EOwnerStatus.FAILED_TO_SEND) {
      return 'Failed to send. Please try again.';
    }

    return '';
  };

  const getTooltip = (status: EOwnerStatus) => {
    if (status === EOwnerStatus.CREATED || status === EOwnerStatus.SENT || status === EOwnerStatus.SKIPPED) {
      return null;
    }

    return (
      <Tooltip
        title={getTooltipTitle(status)}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: '#444444',
              fontSize: 18,
              fontWeight: 'normal',
            },
          },
          arrow: {
            sx: {
              color: '#444444',
            },
          },
        }}
        PopperProps={{
          sx: {
            backgroundColor: 'transparent',
          },
        }}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -4],
                },
              },
            ],
          },
        }}>
        {(status === EOwnerStatus.FAILED_TO_REGENERATE || status === EOwnerStatus.FAILED_TO_SEND) ? (
          <CustomIcon name="warning_outlined" fontSize="small" sx={{ color: '#B3261E' }} />
        ) : (
          <InfoOutlinedIcon fontSize="small" sx={{ color: "#636363" }} />
        )}
      </Tooltip>
    );
  };

  return (
    <Card sx={{ width: '100%', borderRadius: 1, padding: 2.5, marginBottom: 1, boxShadow: '0 0 20px 0 rgba(185, 185, 185, 0.25)' }}>
      <Stack sx={{ paddingBottom: 1.25, borderBottom: '1px solid #D9D9D9' }} gap={1}>
        <span className="owner-card-name-title">{owner_name}</span>
        <span className="owner-card-fsm-details">
          <span className="owner-card-fsm-details-label">FSM:</span> {fsm_name}
        </span>
      </Stack>
      <Stack gap={1} sx={{ paddingTop: 1.25 }}>
        {
          performance && (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" gap={0.75} alignItems="center">
                <CustomIcon name="statistic" sx={{ color: performance.status === EOwnerStatus.SENDING || performance.status === EOwnerStatus.REGENERATING ? '#959595' : '#444444', fontSize: 18 }} />
                <span
                  className={clsx('owner-card-email-type-title', {
                    disabled: performance.status === EOwnerStatus.SENDING || performance.status === EOwnerStatus.REGENERATING,
                  })}
                >
                  Performance Update
                </span>
                {getTooltip(performance.status)}
              </Stack>
              {getPerformanceStackContent()}
            </Stack>
          )
        }
        {performance?.reason && (
          <Stack sx={{ padding: '12px 10px', background: '#F7FBFF', borderLeft: '2px solid #034A95' }}>
            <span className="owner-card-reason">
              <span className="owner-card-reason-label">Reason:</span> {performance.reason}
            </span>
          </Stack>
        )}
        {
          strategy && (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" gap={0.75} alignItems="center">
                <CustomIcon name="camera" sx={{ color: '#444444', fontSize: 20 }} />
                <span className="owner-card-email-type-title">Strategy Update</span>
                {getTooltip(strategy.status)}
              </Stack>
              {getStrategyStackContent()}
            </Stack>
          )
        }
      </Stack>
    </Card>
  );
}
